import { notification } from 'antd';
import api from './service/api';
import { actionLogout } from '../view/system/systemAction';

/*
 * Idle logout (INSA penetration test, finding 4).
 *
 * The worklist polls every 30 seconds and the API client refreshes the token on
 * every 401, so Keycloak's own 30-minute idle timeout never fired: an
 * unattended reading-room PC stayed signed in for as long as the tab was open.
 *
 * "Idle" therefore has to mean "no input from the user", not "no requests".
 * Every window of a login reports input:
 *   - into localStorage, shared by the dashboard and the web viewer (same
 *     origin), so moving between them never counts as idle;
 *   - to the API (POST /api/accounts/activity, at most once a minute), so a
 *     workstation report window in a different browser counts too.
 * A window signs the login out only when its own clock AND the API both say
 * the user has been idle for the whole limit.  If the API cannot be asked, the
 * window does nothing: Keycloak's idle timeout still applies once nothing is
 * refreshing the session.
 *
 * The limit comes from the API (session.idle_timeout_mins, 0 = off).  The
 * viewer's afya-idle-guard.js implements the same rules with the same keys --
 * change both together.
 */

const KEY_ACTIVITY = 'afyanx.lastActivity';
const KEY_HEARTBEAT = 'afyanx.lastHeartbeat';
const KEY_LOGOUT = 'afyanx.idleLogout';
// Test hook: may only SHORTEN the limit, never lengthen it.
const KEY_TEST_LIMIT = 'afyanx.idleTimeoutTestS';
const FLAG_LOGGED_OUT = 'afyanx.idleLoggedOut';

const MAX_LIMIT_MS = 30 * 60 * 1000;
const WARN_BEFORE_MS = 2 * 60 * 1000;
const TICK_MS = 10 * 1000;
const LOCAL_WRITE_EVERY_MS = 5 * 1000;
const HEARTBEAT_EVERY_MS = 60 * 1000;
const NOTICE_KEY = 'afyanx-idle-warning';
// No 'scroll': lists that re-render on a poll can scroll without the user.
const INPUT_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart'];

let started = false;
let loggingOut = false;
let lastLocalWrite = 0;
let checking = false;
let warningShown = false;

const readNumber = (key) => {
  try {
    const v = Number(localStorage.getItem(key));
    return v > 0 ? v : 0;
  } catch (e) {
    return 0;
  }
};

const writeNumber = (key, value) => {
  try {
    localStorage.setItem(key, String(value));
  } catch (e) {
    // Storage blocked: this window still tracks its own input below.
  }
};

let ownLastActivity = Date.now();
const lastActivity = () => Math.max(ownLastActivity, readNumber(KEY_ACTIVITY));

const localLimitMs = () => {
  const test = readNumber(KEY_TEST_LIMIT) * 1000;
  return test > 0 ? Math.min(test, MAX_LIMIT_MS) : MAX_LIMIT_MS;
};

const sendHeartbeat = (now) => {
  if (now - readNumber(KEY_HEARTBEAT) < HEARTBEAT_EVERY_MS) return;
  writeNumber(KEY_HEARTBEAT, now);
  api({ method: 'post', url: '/api/accounts/activity', timeout: 15000 }).catch(() => {});
};

// Browsers can fire mousemove with the pointer standing still (when the page
// re-renders under it, e.g. on every worklist poll); only real movement counts.
let lastX = null;
let lastY = null;

const onInput = (e) => {
  if (e && e.type === 'mousemove') {
    if (e.screenX === lastX && e.screenY === lastY) return;
    lastX = e.screenX;
    lastY = e.screenY;
  }
  const now = Date.now();
  ownLastActivity = now;
  if (warningShown) hideWarning();
  if (now - lastLocalWrite < LOCAL_WRITE_EVERY_MS) return;
  lastLocalWrite = now;
  writeNumber(KEY_ACTIVITY, now);
  sendHeartbeat(now);
};

const hideWarning = () => {
  if (!warningShown) return;
  warningShown = false;
  notification.close(NOTICE_KEY);
};

const showWarning = (remainingMs) => {
  const secs = Math.max(0, Math.round(remainingMs / 1000));
  const when = secs >= 60 ? `about ${Math.ceil(secs / 60)} minute${secs > 60 ? 's' : ''}` : `${secs} seconds`;
  warningShown = true;
  notification.warning({
    key: NOTICE_KEY,
    message: 'You will be signed out soon',
    description: `No activity for a while. For patient privacy you will be signed out in ${when}. Move the mouse or press a key to stay signed in.`,
    duration: 0,
  });
};

const logOut = () => {
  if (loggingOut) return;
  loggingOut = true;
  hideWarning();
  writeNumber(KEY_LOGOUT, Date.now());
  try {
    sessionStorage.setItem(FLAG_LOGGED_OUT, '1');
  } catch (e) {
    // Only the after-login notice is lost.
  }
  actionLogout();
};

const tick = async () => {
  if (loggingOut || checking) return;
  const limit = localLimitMs();
  if (Date.now() - lastActivity() < limit - WARN_BEFORE_MS) {
    hideWarning();
    return;
  }
  // This window has been quiet long enough to matter: ask whether the user is
  // active anywhere else, and what the limit is.
  checking = true;
  try {
    const { data } = await api({ method: 'get', url: '/api/accounts/activity', timeout: 15000 });
    const serverLimit = Number(data && data.idle_timeout_s) * 1000;
    if (!(serverLimit > 0)) {
      hideWarning(); // idle logout switched off on the server
      return;
    }
    const remote = Number(data && data.last_activity_ms) || 0;
    if (remote > readNumber(KEY_ACTIVITY)) writeNumber(KEY_ACTIVITY, remote);
    const effective = Math.min(serverLimit, limit);
    const idle = Date.now() - Math.max(lastActivity(), remote);
    if (idle >= effective) logOut();
    else if (idle >= effective - WARN_BEFORE_MS) showWarning(effective - idle);
    else hideWarning();
  } catch (e) {
    // API unreachable or the session is already gone (the API client handles
    // that): never sign out on a guess.
  } finally {
    checking = false;
  }
};

/** Start idle tracking for a signed-in dashboard window.  Safe to call twice. */
export const startIdleLogout = () => {
  if (started) return;
  started = true;

  // Loading the page is the user acting.
  ownLastActivity = Date.now();
  writeNumber(KEY_ACTIVITY, ownLastActivity);

  INPUT_EVENTS.forEach((type) =>
    window.addEventListener(type, onInput, { capture: true, passive: true })
  );
  // Another window of this browser signed the login out.
  window.addEventListener('storage', (e) => {
    if (e.key === KEY_LOGOUT && e.newValue && !loggingOut) {
      loggingOut = true;
      hideWarning();
      actionLogout();
    }
  });
  setInterval(tick, TICK_MS);

  let wasIdleLoggedOut = false;
  try {
    wasIdleLoggedOut = sessionStorage.getItem(FLAG_LOGGED_OUT) === '1';
    sessionStorage.removeItem(FLAG_LOGGED_OUT);
  } catch (e) {
    // ignore
  }
  if (wasIdleLoggedOut) {
    notification.info({
      message: 'You were signed out',
      description: 'Your previous session ended after 30 minutes without activity.',
      duration: 8,
    });
  }
};
